import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: 'top' | 'bottom';
}

interface UseDropdownPositionOptions {
  estimatedHeight?: number;
  gap?: number;
}

const DEFAULT_ESTIMATED_HEIGHT = 240;
const DEFAULT_GAP = 4;

export function useDropdownPosition(
  triggerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose?: () => void,
  options?: UseDropdownPositionOptions,
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const estimatedHeight = options?.estimatedHeight ?? DEFAULT_ESTIMATED_HEIGHT;
  const gap = options?.gap ?? DEFAULT_GAP;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setPosition(null);
      onCloseRef.current?.();
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlip =
      spaceBelow < estimatedHeight + gap && spaceAbove > spaceBelow;

    if (shouldFlip) {
      setPosition({
        top: rect.top - gap,
        left: rect.left,
        width: rect.width,
        placement: 'top',
      });
    } else {
      setPosition({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        placement: 'bottom',
      });
    }
  }, [triggerRef, estimatedHeight, gap]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  return position;
}
